<script setup lang="ts">
import { useData } from "vitepress";
import { computed } from "vue";
import BrowserFrame from "../../primitives/BrowserFrame.vue";
import ResponsiveImage from "../../primitives/ResponsiveImage.vue";

const { frontmatter } = useData();

const cover = computed<string | undefined>(() => frontmatter.value.cover_image);
const live = computed<string | undefined>(() => frontmatter.value.live);
const title = computed<string>(() => frontmatter.value.title || "");
const desc = computed<string | undefined>(() => frontmatter.value.desc);

// The hero spans the content column, which tops out at --content-width-default
// (72rem / 1152px); below that it is full-bleed minus the page gutter.
const HERO_SIZES = "(min-width: 72rem) 1152px, 100vw";
</script>

<template>
  <header class="project-hero">
    <BrowserFrame v-if="cover" :href="live" class="project-hero-image">
      <ResponsiveImage
        :src="cover"
        :sizes="HERO_SIZES"
        :alt="
          live
            ? `${title} — screenshot of the live site`
            : `${title} — screenshot`
        "
        width="2304"
        height="1296"
        loading="eager"
        fetchpriority="high"
      />
    </BrowserFrame>

    <h1 class="project-hero-title">{{ title }}</h1>
    <p v-if="desc" class="project-hero-desc">{{ desc }}</p>
  </header>
</template>

<style scoped lang="scss">
.project-hero {
  margin-bottom: var(--space-2xl);
}

/* BrowserFrame owns the border, radius, and image sizing; the hero only
   places it. Height comes from the image's own 16:9 aspect plus the chrome
   bar, so there is nothing to crop. */
.project-hero-image {
  margin-bottom: var(--space-xl);
}

.project-hero-title {
  margin: 0 0 var(--space-sm);
}

.project-hero-desc {
  margin: 0;
  color: var(--color-text-muted);
  max-width: var(--content-width-prose);
}
</style>
